import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

const permitResultSchema = z.object({
  jobId: z.number(),
  permitId: z.number().optional(),
  success: z.boolean(),
  message: z.string(),
});

const inboundPermitResponseSchema = z.object({
  message: z.string(),
});

// ---------------------------------------------------------------------------
// Outbound bodies — LITE → CORE
// ---------------------------------------------------------------------------

export const outboundRequestPermitBodySchema = z.object({
  jobId: z.number().int().positive(),
});

export const outboundExtendPermitBodySchema = z.object({
  jobId: z.number().int().positive(),
  permitId: z.number().int().positive(),
  extendDate: z.string().datetime(),
});

export const outboundStartPermitBodySchema = z.object({
  jobId: z.number().int().positive(),
  permitId: z.number().int().positive(),
  startDate: z.string().datetime(),
});

export const outboundStopPermitBodySchema = z.object({
  jobId: z.number().int().positive(),
  permitId: z.number().int().positive(),
  endDate: z.string().datetime(),
});

export type OutboundRequestPermitBody = z.infer<typeof outboundRequestPermitBodySchema>;
export type OutboundExtendPermitBody = z.infer<typeof outboundExtendPermitBodySchema>;
export type OutboundStartPermitBody = z.infer<typeof outboundStartPermitBodySchema>;
export type OutboundStopPermitBody = z.infer<typeof outboundStopPermitBodySchema>;

// ---------------------------------------------------------------------------
// Inbound body — CORE → LITE
// ---------------------------------------------------------------------------

export const inboundPermitBodySchema = z.object({
  meta: z
    .object({
      messageId: z.string().uuid(),
      eventType: z.string(),
      sentAt: z.string(),
      source: z.string().optional(),
      target: z.string().optional(),
      correlationId: z.string().optional(),
      igniteEventId: z.string().optional(),
      schemaVersion: z.string().optional(),
      deliveryPartnerCode: z.string().optional(),
      version: z.string().optional(),
    })
    .passthrough(),
  workOrder: z.object({
    id: z.string(),
    permit: z
      .object({
        permitNumber: z.string().optional(),
        permitReferenceNumber: z.string().optional(),
        // Legacy — earlier CORE builds used this key. Accepted so in-flight
        // messages don't fail schema validation.
        permitReference: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        actualStart: z.string().nullish(),
        extendDate: z.string().optional(),
        // Permit category resolved by CORE after the SAP DISP confirmation.
        // Present on the "Permit Category" event, which carries no
        // permitRequestType and does not touch permit lifecycle rows.
        permitCategory: z.enum(['planned', 'immediate', 'none']).optional(),
      })
      .passthrough(),
    // Optional: the Permit Category event omits it (identified by
    // permit.permitCategory instead). Lifecycle events always set it.
    permitRequestType: z.enum(['initial', 'extension', 'start', 'stop', 'general']).optional(),
  }),
});

export type InboundPermitBody = z.infer<typeof inboundPermitBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const workOrderPermitDirectionalContract = c.router({
  // OUTBOUND — LITE → CORE
  outboundRequestPermit: {
    method: 'POST' as const,
    path: '/outbound/request-permit',
    body: outboundRequestPermitBodySchema,
    responses: {
      200: permitResultSchema,
      403: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: request permit',
    description:
      'Send work_order.permit_request (action=request) to CORE for a job. Logs to communication_log.',
  },
  outboundExtendPermit: {
    method: 'POST' as const,
    path: '/outbound/extend-permit',
    body: outboundExtendPermitBodySchema,
    responses: {
      200: permitResultSchema,
      403: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: extend permit',
    description:
      'Send work_order.permit_request (action=extend) to CORE. Validates the permit is linked to the job, updates extendDate on the permit record, logs to communication_log.',
  },
  outboundStartPermit: {
    method: 'POST' as const,
    path: '/outbound/start-permit',
    body: outboundStartPermitBodySchema,
    responses: {
      200: permitResultSchema,
      403: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: start permit',
    description:
      'Send work_order.permit_request (action=start) to CORE. Validates the permit is linked to the job, logs to communication_log. DB startDate is updated when CORE confirms via inbound.',
  },
  outboundStopPermit: {
    method: 'POST' as const,
    path: '/outbound/stop-permit',
    body: outboundStopPermitBodySchema,
    responses: {
      200: permitResultSchema,
      403: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Outbound: stop permit',
    description:
      'Send work_order.permit_request (action=stop) to CORE. Validates the permit is linked to the job, logs to communication_log. DB endDate is updated when CORE confirms via inbound.',
  },

  // INBOUND — CORE → LITE
  inboundPermitUpdate: {
    method: 'POST' as const,
    path: '/inbound/permit-update',
    body: inboundPermitBodySchema,
    responses: {
      200: inboundPermitResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Inbound: permit update',
    description:
      'Receives work_order.permit_update from CORE. If permit.permitCategory is present (and permitRequestType absent), records the permit category on the job. Otherwise routes on permitRequestType: initial (create permit record), extension (update extendDate), start (update startDate), stop (update endDate), general (apply any provided date fields).',
  },
});
